import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CheckCircle2,
  CloudUpload,
  FileText,
  Gauge,
  Inbox,
  LoaderCircle,
  RefreshCw,
  Scale,
  Settings2,
  SlidersHorizontal,
  Smile,
  Sparkles
} from "lucide-react";
import { useState } from "react";
import {
  resolveApiAssetUrl,
  type CompanyContext,
  type MentionSummary,
  type OverviewAttention,
  type PolarityCount,
  type UploadSummary
} from "@/shared/api/api-client";
import { AppLink } from "@/shared/components/AppLink";
import { CompanyChecklistCard } from "@/features/companies/CompanyChecklistCard";
import type { CompanyChecklist } from "@/shared/api/api-client";
import { SummaryPeriodPicker } from "@/shared/components/SummaryPeriodPicker";
import { todayInInstallation } from "@/shared/utils/installation-clock";
import { summaryPeriodLabel, summaryWindowText, type SummaryPeriod } from "@/shared/utils/summary-window";
import { ACTIVE_STATUSES, statusMeta } from "@/features/uploads/upload-shared";
import { IfCena, useShowsCena } from "@/shared/components/cena-visibility";
import { mentionPlatform } from "@/shared/components/mention-platform";
import { SentimentBadge } from "@/shared/components/SentimentBadge";
import type { Mention } from "@/shared/types/mention";
import { polarityTone } from "@/shared/types/polarity";

import { formatDecimal, formatInteger, formatSignedCompact } from "@/shared/utils/format";
import { mentionFilterSearch } from "@/features/mentions/mention-filter-link";
import { AttentionEditor } from "./AttentionEditor";
import {
  ATTENTION_LINES,
  DEFAULT_OVERVIEW_ATTENTION,
  evaluateLine,
  lineApplies,
  type AttentionCounts,
  type AttentionRow
} from "./attention-lines";
import { RollingNumber } from "./RollingNumber";
type OverviewTarget = "mencoes" | "uploads" | "dashboard" | "configuracoes" | "relatorios/cena";

type OverviewPageProps = {
  company: CompanyContext;
  /** Amostra que alimenta SÓ a lista "menções que pedem contexto" — os
   * números vêm do `summary`. */
  mentions: Mention[];
  /** Agregados do servidor sobre a janela de 30 dias. `null` quando a
   * chamada falhou: os cartões mostram "—" e o resto da tela segue. */
  summary: MentionSummary | null;
  uploads: UploadSummary[];
  loading: boolean;
  error: string | null;
  /** `true` enquanto algum upload está na fila/processando — a tela
   * mostra que os números estão vivos em vez de parecerem finais. */
  processing: boolean;
  /** Destino de cada atalho da tela. Links, e não callbacks (2026-10-26):
   * valem clique do meio e "abrir em nova aba". Vêm da rota, que conhece a
   * empresa. */
  hrefFor: (target: OverviewTarget) => string;
  changeCompanyHref: string;
  settingsHref?: string;
  openMention: (mention: Mention) => void;
  reload: () => void;
  /** O período escolhido (1, 7, 15 ou 30 dias até o último dado). */
  days: SummaryPeriod;
  onDaysChange: (days: SummaryPeriod) => void;
  /** A lista "Configuração da empresa" (calculada no servidor). Sem ela
   * (testes de apresentação) o card não aparece. */
  checklist?: CompanyChecklist | null;
  checklistLoading?: boolean;
  checklistError?: string | null;
  onChecklistRetry?: () => void;
  /** Link de Ajustes pra aba que resolve cada item. */
  checklistSettingsHref?: (tab: string) => string;
  /** A ação "Criar canais padrão"; ausente pra quem só lê. */
  onStandardChannels?: () => Promise<string>;
  /** Quais linhas do painel "Pontos de atenção" aparecem e em que limite
   * cada uma acende (2026-11-01; quinze linhas desde 2026-11-02).
   * Ausente = o painel de sempre. */
  attention?: OverviewAttention;
  /** Salva o ajuste do painel; ausente pra quem está abaixo de
   * Especialista, e aí o botão de ajustar nem aparece. */
  onSaveAttention?: (next: OverviewAttention) => Promise<void>;
  /** Os números das linhas contadas à parte (`GET /mentions/count`). */
  attentionCounts?: AttentionCounts;
  /** Quando vence cada rascunho de amostra pendente (a linha de
   * rascunhos); `null` = a leitura falhou. */
  draftExpiries?: string[] | null;
  /** A lista de rascunhos é de Técnico pra cima: abaixo disso a linha
   * não aparece, nem ligada. */
  draftsVisible?: boolean;
};

// Status reais de `Upload.status` (`core/database/models/pipeline.py`) —
// eram os do backend descartado (`concluido`/`validando`/…), que nunca
// bateriam com o que o web_api devolve.
//
// O rótulo vem de `upload-shared`, que é o mesmo que as telas de upload
// usam. Aqui havia uma SEGUNDA cópia do mapa, e a função que a
// consultava passava o status por `toLocaleLowerCase` antes de procurar
// numa chave MAIÚSCULA: nunca acertava, caía no fallback e a Visão
// Geral mostrava "FINISHED WITH WARNINGS" onde o resto do sistema
// mostra "Concluído com pendências".
const failedStatuses = new Set(["ERRORED", "INVALID_TEMPLATE", "INVALID_FILE"]);
const warningStatuses = new Set(["FINISHED_WITH_WARNINGS"]);

/**
 * O cartão de sentimento mostra as polaridades do domínio uma a uma
 * (`summary.polarity_counts`), em vez dos dois baldes de tom que o
 * precederam — "positivas" e "negativas" escondiam metade dos quatro
 * níveis.
 *
 * **O percentual é sobre as CLASSIFICADAS** (a soma da própria lista),
 * nunca sobre o total da janela: menção sem polaridade não é neutra nem
 * nada, é "ainda sem classificação", e jogá-la no denominador faria
 * toda polaridade parecer menor do que é. Ela aparece à parte, no
 * rodapé. O NEUTRO só entra quando há menção: numa empresa de 4
 * níveis ele é resíduo, e uma linha zerada de "Neutro" seria ruído.
 */
function SentimentCard({ summary }: { summary: MentionSummary | null }) {
  const shown: PolarityCount[] = (summary?.polarity_counts ?? []).filter(
    entry => entry.palette !== "neutral" || entry.count > 0
  );
  const classified = shown.reduce((sum, entry) => sum + entry.count, 0);
  const unclassified = summary?.unclassified_count ?? 0;
  // Janela sem menção nenhuma: não há o que classificar, então dizer
  // "todas classificadas" seria contradizer o texto do vazio.
  const windowIsEmpty = summary?.total_mentions === 0;
  const share = (count: number) => {
    const exact = (count / classified) * 100;
    // Contagem pequena demais pra aparecer como inteiro não vira "0%":
    // isso diria que não há nenhuma.
    return count > 0 && exact < 0.5 ? "<1%" : `${formatInteger(Math.round(exact))}%`;
  };
  const barLabel = classified
    ? `Sentimento das menções classificadas: ${shown
        .map(entry => `${entry.label} ${formatInteger(entry.count)} (${share(entry.count)})`)
        .join(", ")}`
    : "Nenhuma menção classificada na janela";

  return (
    <article className="metric-sentiment">
      <div><span>Sentimento</span><i><Smile size={18} aria-hidden="true"/></i></div>
      <span className="sentiment-bar" role="img" aria-label={barLabel}>
        {classified > 0 && shown.filter(entry => entry.count > 0).map(entry => (
          <span
            key={entry.name}
            className={`${entry.palette}-bg`}
            data-palette={entry.palette}
            style={{ flexGrow: entry.count }}
          />
        ))}
      </span>
      {classified > 0 ? (
        <ul className="sentiment-rows">
          {shown.map(entry => (
            <li key={entry.name} data-palette={entry.palette}>
              <i className={`sentiment-dot ${entry.palette}-bg`} aria-hidden="true"/>
              <span>{entry.label}</span>
              <b><RollingNumber value={formatInteger(entry.count)} delay={500} /></b>
              <small>{share(entry.count)}</small>
            </li>
          ))}
        </ul>
      ) : (
        <p className="sentiment-empty">
          {!summary
            ? "Indicador indisponível"
            : windowIsEmpty
              ? "Nenhuma menção na janela"
              : "Nenhuma menção classificada na janela"}
        </p>
      )}
      {summary && !windowIsEmpty && (
        <p>
          {unclassified > 0
            ? `${formatInteger(unclassified)} sem classificação`
            : "Todas classificadas"}
        </p>
      )}
    </article>
  );
}

/**
 * A frase que diz de que período são os números da tela. Os cartões e o
 * painel de atenção leem a MESMA janela do resumo. Desde 2026-10-19 ela
 * termina no dia do ÚLTIMO DADO, não hoje, e a frase diz os dois — ver
 * `shared/utils/summary-window.ts`.
 */
/** Classe de tom do saldo reputacional pelo sinal; ausente é neutro. */
function balanceTone(value: number | null | undefined): string {
  if (value == null || value === 0) return "is-neutral";
  return value > 0 ? "is-positive" : "is-negative";
}

function windowNote(summary: MentionSummary | null, days: number): string {
  if (!summary) return `Os indicadores consideram ${summaryPeriodLabel(days)} até o dado mais recente.`;
  return summaryWindowText(summary, todayInInstallation());
}

export function OverviewPage({
  company,
  mentions,
  summary,
  uploads,
  loading,
  error,
  processing,
  hrefFor,
  changeCompanyHref,
  settingsHref,
  openMention,
  reload,
  days,
  onDaysChange,
  checklist = null,
  checklistLoading = false,
  checklistError = null,
  onChecklistRetry,
  checklistSettingsHref,
  onStandardChannels,
  attention = DEFAULT_OVERVIEW_ATTENTION,
  onSaveAttention,
  attentionCounts = {},
  draftExpiries,
  draftsVisible = false
}: OverviewPageProps) {
  const showsCena = useShowsCena();
  const [editingAttention, setEditingAttention] = useState(false);
  /**
   * Todos os números desta tela saem do SERVIDOR desde 2026-08-12.
   *
   * Antes eram média e contagem sobre as 50 menções mais recentes, sem
   * recorte de data — o que produzia três defeitos ao mesmo tempo:
   * "312 desfavoráveis" no cartão e "3" no painel logo abaixo, média de
   * CENA contando menção sem nota como zero, e um total de volume que
   * não tinha relação com o denominador dos percentuais.
   *
   * A amostra continua existindo, mas só alimenta a LISTA.
   */
  const totalMentions = summary?.total_mentions ?? 0;
  const averageCena = summary?.average_cena ?? null;
  const windowDays = summary?.window_days ?? days;

  const pendingUploads = uploads.filter(upload => ACTIVE_STATUSES.has(upload.status));
  const failedUploads = uploads.filter(upload => failedStatuses.has(upload.status));
  const warningUploads = uploads.filter(upload => warningStatuses.has(upload.status));
  const uploadIssues = failedUploads.length + warningUploads.length;
  /**
   * Cada linha do painel ACENDE quando está ligada e o número passa do
   * limite da empresa (`attention-lines.ts`). Abaixo do limite a linha
   * continua com o número, mas sem alerta.
   *
   * O topo conta PONTOS acesos, não menções (2026-11-02): com quinze
   * linhas a soma deixou de significar alguma coisa — "negativas" e
   * "negativas no Tier 1" contam as mesmas menções, e percentual e dias
   * não se somam a itens.
   */
  const applicability = {
    showsCena,
    polarityScale: company.polarity_scale ?? "four_level",
    draftsVisible
  };
  const attentionRows: AttentionRow[] = ATTENTION_LINES
    .filter(line => attention[line.key]?.enabled && lineApplies(line.key, applicability))
    .map(line =>
      evaluateLine(line.key, {
        attention,
        summary,
        pendingUploads: pendingUploads.length,
        uploadIssues,
        counts: attentionCounts,
        draftExpiries,
        today: todayInInstallation(),
        now: Date.now()
      })
    );
  const attentionCount = attentionRows.filter(row => row.lit).length;
  const rowHref = (row: AttentionRow): string => {
    switch (row.target.kind) {
      case "mentions":
        return hrefFor("mencoes") + (row.target.criteria ? mentionFilterSearch(row.target.criteria) : "");
      case "uploads":
        return hrefFor("uploads");
      case "dashboard":
        return hrefFor("dashboard");
      case "drafts":
        return hrefFor("relatorios/cena");
    }
  };
  const criticalMentions = [...mentions]
    .sort((left, right) => {
      const leftNegative = polarityTone(left.polarity) === "negative";
      const rightNegative = polarityTone(right.polarity) === "negative";
      if (leftNegative && !rightNegative) return -1;
      if (rightNegative && !leftNegative) return 1;
      // Menção sem nota vai pro FIM. Com `?? 0` (como era até
      // 2026-08-12) todo post de rede empatava em zero e dominava o
      // topo da lista — que deveria mostrar as piores de imprensa.
      if (left.cena === null) return right.cena === null ? 0 : 1;
      if (right.cena === null) return -1;
      return left.cena - right.cena;
    })
    .slice(0, 3);

  return (
    <div className="operational-home" aria-busy={loading}>
      <header className="operational-hero">
        <div className="operational-hero-copy">
          <span className="eyebrow">CENTRAL DE INTELIGÊNCIA</span>
          <div className="operational-hero-title">
            <div className={`company-card-mark hero-brand-mark ${company.logo_url ? "has-logo" : ""}`} aria-hidden="true">
              {company.logo_url
                ? <img src={resolveApiAssetUrl(company.logo_url)} alt=""/>
                : company.name.slice(0, 2).toUpperCase()}
            </div>
            <h1>{company.name}</h1>
            <div className={`hero-status-inline ${attentionCount ? "attention" : "healthy"}`} role="status">
              <i aria-hidden="true"/>
              <span>
                {attentionCount
                  ? `${formatInteger(attentionCount)} ${attentionCount === 1 ? "ponto" : "pontos"} de atenção`
                  : "Em dia"}
              </span>
            </div>
          </div>
          <p>Acompanhe a reputação, priorize revisões e continue o trabalho de onde parou.</p>
        </div>
        <div className="operational-hero-side">
          <div className="operational-actions">
            <AppLink className="primary-button" to={hrefFor("uploads")}>
              <CloudUpload size={16} aria-hidden="true"/>Enviar arquivo
            </AppLink>
            <button
              className="icon-action-btn"
              onClick={reload}
              type="button"
              data-tooltip="Atualizar dados"
              title="Atualizar dados"
              aria-label="Atualizar dados"
            >
              <RefreshCw size={15} />
            </button>
            {settingsHref && (
              <AppLink
                className="icon-action-btn"
                to={settingsHref}
                data-tooltip="Abrir ajustes da empresa"
                title="Abrir ajustes da empresa"
                aria-label="Abrir ajustes da empresa"
              >
                <Settings2 size={15} />
              </AppLink>
            )}
            <AppLink
              className="icon-action-btn"
              to={changeCompanyHref}
              data-tooltip="Trocar empresa"
              title="Trocar empresa"
              aria-label="Trocar empresa"
            >
              <Building2 size={15} />
            </AppLink>
          </div>
        </div>
      </header>

      <div aria-live="polite" aria-atomic="true">
        {loading && <div className="operational-state" role="status"><LoaderCircle className="spin" size={19} aria-hidden="true"/>Atualizando dados da empresa…</div>}
        {error && <div className="operational-state error" role="alert"><span>{error}</span><button onClick={reload}>Tentar novamente</button></div>}
      </div>
      {!loading && !error && !summary && (
        <div className="operational-state">
          Não foi possível carregar os indicadores agora. O restante da
          página continua atualizado.
        </div>
      )}

      <div className="operational-window">
        <SummaryPeriodPicker days={days} onChange={onDaysChange} />
        <p className="operational-window-note">{windowNote(summary, days)}</p>
      </div>

      {/* O card só aparece quando algo falta ou pede atenção; com tudo em
          ordem, uma linha discreta (a falha de carga também fica no card). */}
      {checklistSettingsHref && (checklistError || (checklist && (checklist.missing > 0 || checklist.warning > 0))) ? (
        <CompanyChecklistCard
          checklist={checklist}
          loading={checklistLoading}
          error={checklistError}
          onRetry={onChecklistRetry}
          settingsHref={checklistSettingsHref}
          onStandardChannels={onStandardChannels}
          companyId={company.id}
        />
      ) : checklist ? (
        <p className="operational-checklist-done" role="status">Configuração da empresa completa</p>
      ) : null}

      <section className="operational-metrics" aria-label={`Indicadores dos últimos ${windowDays} dias`}>
        <IfCena>
        <article className="metric-cena">
          <div><span>Índice CENA médio</span><i><Gauge size={18} aria-hidden="true"/></i></div>
          <strong><RollingNumber value={averageCena === null ? "—" : formatDecimal(averageCena)} delay={200} /><small>/10</small></strong>
          <meter
            min="0"
            max="10"
            value={averageCena ?? 0}
            aria-label={averageCena === null
              ? "Índice CENA médio indisponível"
              : `Índice CENA médio: ${formatDecimal(averageCena)} de 10`}
          />
          {/* O `icena` vivo só existe pra imprensa — post de rede só
              ganha nota dentro de amostra. Numa empresa só social o
              cartão fica em "—" pra sempre, e sem esta frase isso lê
              como sistema quebrado. */}
          <p>
            {summary && summary.press_total_mentions > 0
              ? `Sobre ${formatInteger(summary.press_total_mentions)} menções de imprensa`
              : "Sem menções de imprensa na janela"}
          </p>
        </article>
        <article className="metric-cena-balance">
          <div><span>Saldo reputacional</span><i><Scale size={18} aria-hidden="true"/></i></div>
          <strong
            className={balanceTone(summary?.cena_balance)}
            title={summary?.cena_balance == null ? undefined : formatInteger(summary.cena_balance)}
          >
            {formatSignedCompact(summary?.cena_balance)}
          </strong>
          <p>
            {summary && summary.scored_mentions > 0
              ? `Soma do ESCena de ${formatInteger(summary.scored_mentions)} menções com nota`
              : "Nenhuma menção com nota na janela"}
          </p>
        </article>
        </IfCena>
        <SentimentCard summary={summary}/>
        <article className="metric-volume">
          <div><span>Volume monitorado</span><i><Inbox size={18} aria-hidden="true"/></i></div>
          <strong><RollingNumber value={formatInteger(totalMentions)} delay={280} /></strong>
          {/* Enquanto a esteira processa, o número SOBE — dizer isso
              evita o usuário ler um total parcial como se fosse final
              (a tela se atualiza sozinha, sem F5). */}
          {processing ? (
            <p className="metric-live"><LoaderCircle className="spin" size={12}/>Processando — o total ainda está subindo</p>
          ) : (
            <p>Menções na janela</p>
          )}
        </article>
      </section>

      <section className="operational-layout">
        <article className="card attention-panel">
          <div className="section-heading">
            <div><span className="eyebrow">AGORA</span><h2>Pontos de atenção</h2></div>
            {onSaveAttention && !editingAttention && (
              <button
                className="icon-action-btn"
                onClick={() => setEditingAttention(true)}
                type="button"
                data-tooltip="Ajustar pontos de atenção"
                title="Ajustar pontos de atenção"
                aria-label="Ajustar pontos de atenção"
              >
                <SlidersHorizontal size={15} />
              </button>
            )}
          </div>
          {editingAttention && onSaveAttention ? (
            <AttentionEditor
              attention={attention}
              visible={key => lineApplies(key, { ...applicability, draftsVisible: true })}
              onSave={onSaveAttention}
              onClose={() => setEditingAttention(false)}
            />
          ) : (
          <div className="attention-list">
            {/* As linhas do resumo usam os mesmos números dos cartões
                acima, da mesma fonte — enquanto o painel contava a amostra
                e o cartão contava outra coisa, a tela mostrava dois valores
                com o mesmo rótulo a 200px de distância. Cada linha leva ao
                próprio recorte (`?filtro=`), no mesmo período. */}
            {attentionRows.map(row => (
              <AppLink
                key={row.key}
                className={row.lit || row.busy ? "attention-row warning" : "attention-row clear"}
                to={rowHref(row)}
                data-line={row.key}
              >
                <span>
                  {row.lit
                    ? row.key === "unclassified_mentions"
                      ? <Sparkles size={18} aria-hidden="true"/>
                      : <AlertTriangle size={18} aria-hidden="true"/>
                    : row.busy
                      ? <LoaderCircle size={18} aria-hidden="true"/>
                      : <CheckCircle2 size={18} aria-hidden="true"/>}
                </span>
                <div><strong>{row.title}</strong><small>{row.detail}</small></div>
                <ArrowRight size={16} aria-hidden="true"/>
              </AppLink>
            ))}
            {!attentionRows.length && (
              <div className="operational-empty-compact">Nenhum ponto de atenção ligado para esta empresa.</div>
            )}
          </div>
          )}
        </article>

        <article className="card quick-actions-panel">
          <div className="section-heading"><div><span className="eyebrow">ATALHOS</span><h2>Continuar trabalhando</h2></div></div>
          <div className="quick-action-grid">
            <AppLink to={hrefFor("uploads")}><i><CloudUpload size={19} aria-hidden="true"/></i><span><strong>Novo upload</strong><small>Importar planilhas</small></span><ArrowRight size={15} aria-hidden="true"/></AppLink>
            <AppLink to={hrefFor("mencoes")}><i><Inbox size={19} aria-hidden="true"/></i><span><strong>Revisar menções</strong><small>Classificar publicações</small></span><ArrowRight size={15} aria-hidden="true"/></AppLink>
            <AppLink to={hrefFor("dashboard")}><i><Gauge size={19} aria-hidden="true"/></i><span><strong>Abrir dashboard</strong><small>Análises consolidadas</small></span><ArrowRight size={15} aria-hidden="true"/></AppLink>
            <AppLink to={hrefFor("configuracoes")}><i><FileText size={19} aria-hidden="true"/></i><span><strong>Configurar empresa</strong><small>Regras e parâmetros</small></span><ArrowRight size={15} aria-hidden="true"/></AppLink>
          </div>
        </article>

        <article className="card recent-mentions-panel">
          <div className="section-heading">
            <div><span className="eyebrow">MONITORAMENTO</span><h2>Menções que pedem contexto</h2></div>
            <AppLink className="text-button" to={hrefFor("mencoes")}>Ver todas <ArrowRight size={14} aria-hidden="true"/></AppLink>
          </div>
          <div className="operational-mention-list">
            {criticalMentions.map(mention => {
              const platform = mentionPlatform(mention);
              const PlatformIcon = platform.Icon;
              return (
                <button key={mention.id} onClick={() => openMention(mention)}>
                  <div className={`mention-channel-badge platform-${platform.tone}`} title={platform.label}>
                    <PlatformIcon size={16} aria-hidden="true"/>
                  </div>
                  <div><strong>{mention.title}</strong><span>{mention.source} · {mention.date}</span></div>
                  <div className="mention-trailing">
                    <SentimentBadge value={mention.sentiment} polarity={mention.polarity}/>
                    {showsCena && mention.cena !== null && (
                      <b aria-label={`CENA ${formatDecimal(mention.cena)} de 10`}>{formatDecimal(mention.cena)}</b>
                    )}
                  </div>
                </button>
              );
            })}
            {!criticalMentions.length && <div className="operational-empty-compact">Nenhuma menção disponível para esta empresa.</div>}
          </div>
        </article>

        <article className="card recent-uploads-panel">
          <div className="section-heading">
            <div><span className="eyebrow">DADOS</span><h2>Uploads recentes</h2></div>
            <AppLink className="text-button" to={hrefFor("uploads")}>Abrir uploads <ArrowRight size={14} aria-hidden="true"/></AppLink>
          </div>
          <div className="operational-upload-list">
            {uploads.slice(0, 4).map(upload => (
              <div key={upload.id}>
                <span className={`upload-status-dot status-${upload.status.toLocaleLowerCase("pt-BR")}`} aria-hidden="true"/>
                <div>
                  <strong>#{upload.id} · {upload.clipping_service ?? "sem modelo"}</strong>
                  <small>
                    {upload.processed_rows}/{upload.total_rows ?? "?"} linhas
                    {upload.error_count > 0 && ` · ${upload.error_count} erros`}
                  </small>
                </div>
                <span className="plain-tag">{statusMeta(upload.status, upload.is_advancing).label}</span>
              </div>
            ))}
            {!uploads.length && (
              <div className="operational-empty-compact">
                <span>Nenhum arquivo enviado ainda.</span>
                <AppLink to={hrefFor("uploads")}>Fazer primeiro upload</AppLink>
              </div>
            )}
          </div>
        </article>
      </section>
    </div>
  );
}
